import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { LocaleContext, useDateTimeFormat, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { sendParticipantNotification } from './helpers/send_participant_notification.helpers';
import { getApiErrorMessage } from '../../helpers/api.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
/** Supported send delays for the Send notification modal (minutes). */
export const NOTIFICATION_SEND_OFFSETS = [0, 5, 15, 30, 60];
export const isNotificationSendOffset = (value) => NOTIFICATION_SEND_OFFSETS.includes(value);
/**
 * Sends or schedules a study notification (AXONCONFIG-5254). Shows a toast and
 * refreshes the timeline on success. Re-entry is prevented by the submit button's
 * `disabled={isSending}` — do not rely on `isSending` state alone inside `send`.
 */
export const useSendNotification = () => {
    const { environment } = useCurrentEnvironment();
    const queryClient = useQueryClient();
    const translate = useLocalizeMessage();
    const { formatDateTime } = useDateTimeFormat();
    const { addNotification } = useToastNotification();
    const locale = useContext(LocaleContext);
    const [isSending, setIsSending] = useState(false);
    const send = async (input) => {
        setIsSending(true);
        try {
            if (!environment) {
                throw new Error('Environment not defined');
            }
            const result = await sendParticipantNotification({
                environment,
                siteId: input.siteId,
                participantId: input.participantId,
                notificationName: input.notificationName,
                offsetMinutes: input.offsetMinutes,
                locale,
            });
            addNotification({
                title: '',
                subtitle: result.status === 'sent'
                    ? translate('status.timeline.notification_sent')
                    : translate('status.timeline.notification_scheduled', { time: formatDateTime(result.sendAt) }),
                type: 'success',
            });
            queryClient
                .invalidateQueries({
                queryKey: ['participantEventTimeline', environment.id, input.siteId, input.participantId, locale],
            })
                .catch(() => { });
            return result;
        }
        catch (error) {
            addNotification({
                title: '',
                subtitle: getApiErrorMessage(error, true) ?? translate('error.generic'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsSending(false);
        }
    };
    return {
        send,
        isSending,
    };
};
